"use client"

import { cn } from "@/lib/utils"
import { Invoice } from "@phosphor-icons/react"
import { motion } from "@/components/insurance/static-motion"
import { useInsuranceTranslation } from "@/lib/insurance/use-insurance-translation"
import { formatCHF } from "@/lib/insurance/insurers"
import {
  buildBookingUrl,
  isMembershipService,
} from "@/lib/insurance/booking-deeplink"
import { trackEvent } from "@/lib/insurance/insurance-tracking"
import { useBookingFlow } from "@/components/layout/booking-flow-provider"
import { useCountry } from "@/components/layout/country-provider"
import { CoverageComparison } from "@/components/insurance/coverage-comparison"
import type { Service } from "@/lib/insurance/insurance-types"
import type { RankedInsurer } from "@/lib/insurance/rank-insurers"

const EASE_OUT_QUART = [0.25, 1, 0.5, 1] as const

const fadeUp = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 6 },
  transition: { duration: 0.35, ease: EASE_OUT_QUART },
}

type ResultType = "known" | "not_covered" | "unknown" | "likely"
type ProformaStatus =
  | "idle"
  | "submitting"
  | "success"
  | "error"
  | "async_fallback"

export interface CoverageResultProps {
  embedded: boolean
  /** Card shell classes — shared with the form so every phase matches. */
  cardClass: string
  resultRef: React.RefObject<HTMLDivElement | null>
  resultType: ResultType

  // Coverage values (computed in the parent's render)
  service: Service | undefined
  /** What the visitor pays for `service` — the member rate when a membership
   *  tier is selected alongside it. The breakdown has to quote this and not
   *  `service.price`, or the line above the total contradicts the total. */
  servicePrice: number
  /** The membership tier bought alongside the check-up: its yearly price, 0
   *  when there is none (or when the membership IS the quoted service, in
   *  which case it is already the `servicePrice` line). It gets its own
   *  breakdown row — folded into "add-ons" a yearly subscription reads as an
   *  extra on the scan, and left out entirely "you pay" understated what the
   *  visitor will actually be charged. */
  membershipPrice: number
  /** Localised tier name, e.g. "2x per year". Not the breakdown row's label
   *  (that says "Membership" — a rhythm in a column of prices reads as a
   *  quantity); it names the tier in the cross-insurer comparison's subtitle,
   *  where there is room and the reader needs to know which one. */
  membershipName?: string
  // Cross-insurer ranking (AHD-8461). Empty when the section should not show
  // (unknown outcome / "Other" insurer) — the comparison renders nothing.
  ranked: RankedInsurer[]
  coveragePeriod: string | undefined
  totalReimbursement: number
  addonTotalPrice: number
  outOfPocket: number
  likelyReimbursement: number | null
  likelyOutOfPocket: number
  insurerDisplayName: string
  packageDisplayName: string
  isPackageNotCovered: boolean
  coverageInfoUrl: string | null
  selectedAddonProductIds: string[]
  insurerId: string
  serviceId: string
  email: string
  showProformaButton: boolean

  // Pro forma request cluster
  showProformaForm: boolean
  setShowProformaForm: (v: boolean) => void
  proformaFirstName: string
  setProformaFirstName: (v: string) => void
  proformaLastName: string
  setProformaLastName: (v: string) => void
  proformaGender: string
  setProformaGender: (v: string) => void
  proformaStatus: ProformaStatus
  proformaPdfUrl: string | null
  proformaError: string | null
  setProformaError: (v: string | null) => void
  setProformaStatus: (v: ProformaStatus) => void
  handleProformaRequest: () => void

  handleReset: () => void
}

// ── Result screen ──
// Extracted from coverage-form.tsx (AHD-7879) to keep the form's main render
// tree small. Renders as plain DOM via the static-motion shim (no framer
// runtime) — the entrance reveals are CSS in coverage-form.css.
export function CoverageResult({
  embedded,
  cardClass,
  resultRef,
  resultType,
  service,
  servicePrice,
  membershipPrice,
  membershipName,
  ranked,
  coveragePeriod,
  totalReimbursement,
  addonTotalPrice,
  outOfPocket,
  likelyReimbursement,
  likelyOutOfPocket,
  insurerDisplayName,
  packageDisplayName,
  isPackageNotCovered,
  coverageInfoUrl,
  selectedAddonProductIds,
  insurerId,
  serviceId,
  email,
  showProformaButton,
  showProformaForm,
  setShowProformaForm,
  proformaFirstName,
  setProformaFirstName,
  proformaLastName,
  setProformaLastName,
  proformaGender,
  setProformaGender,
  proformaStatus,
  proformaPdfUrl,
  proformaError,
  setProformaError,
  setProformaStatus,
  handleProformaRequest,
  handleReset,
}: CoverageResultProps) {
  const { t, locale } = useInsuranceTranslation()
  const { code: country } = useCountry()
  const bookingFlow = useBookingFlow()

  // Compare nudge (AHD-8461): a teaser above the CTAs summarising the best
  // insurer and how much more it reimburses than the user's plan, scrolling to
  // the full comparison. Only shown when there's a strictly better option.
  const topInsurer = ranked[0]
  const userReimbursement =
    resultType === "known"
      ? totalReimbursement
      : resultType === "likely"
        ? (likelyReimbursement ?? 0)
        : 0
  const compareDelta = topInsurer ? topInsurer.chf - userReimbursement : 0
  const showCompareNudge =
    !!topInsurer &&
    (topInsurer.status === "confirmed" || topInsurer.status === "likely") &&
    compareDelta > 0

  return (
    <section className={embedded ? "py-4" : "py-16"}>
      <div ref={resultRef} className={cardClass} style={{ minHeight: 480 }}>
        <motion.div {...fadeUp} className="text-center">
          <div className="success-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="success-desc">
            {resultType === "known"
              ? t("gate.success.desc.known", { email })
              : resultType === "not_covered"
                ? t("gate.success.desc.not_covered", { email })
                : resultType === "likely"
                  ? t("gate.success.desc.likely", { email })
                  : t("gate.success.desc", { email })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="mt-6"
        >
          {resultType === "known" && service ? (
            <div className="result-panel-known">
              {/* AHD-7033 — headline reframed from "Estimated coverage" to
                  "You save" (benefit framing). Same CHF figure underneath
                  (the reimbursement amount). */}
              <div className="result-estimate-label">
                {t("gate.result.label.you_save")}
              </div>
              <div className="result-amount">
                CHF {formatCHF(totalReimbursement)}
              </div>
              <div className="result-breakdown">
                {/* Membership first: it is the commitment the check-up is
                    priced against, so it reads before the price it earned.
                    Labelled by product, not by rhythm — "2x per year" in a
                    row of prices reads as a quantity, not as what it is. */}
                {membershipPrice > 0 && (
                  <BreakdownItem
                    label={t("gate.result.breakdown.membership")}
                    value={`CHF ${formatCHF(membershipPrice)}`}
                    delay={0.3}
                  />
                )}
                <BreakdownItem
                  label={t("gate.result.breakdown.service")}
                  value={`CHF ${formatCHF(servicePrice)}`}
                  delay={membershipPrice > 0 ? 0.32 : 0.3}
                />
                {addonTotalPrice > 0 && (
                  <BreakdownItem
                    label={t("gate.result.breakdown.addons")}
                    value={`CHF ${formatCHF(addonTotalPrice)}`}
                    delay={0.35}
                  />
                )}
                <BreakdownItem
                  label={t("gate.result.breakdown.save")}
                  value={`CHF ${formatCHF(totalReimbursement)}`}
                  savings
                  delay={addonTotalPrice > 0 ? 0.4 : 0.4}
                />
                <BreakdownItem
                  label={t("gate.result.breakdown.pay")}
                  value={`CHF ${formatCHF(outOfPocket)}`}
                  delay={addonTotalPrice > 0 ? 0.45 : 0.5}
                />
              </div>
              <GuaranteeNote
                text={t("gate.result.guarantee", {
                  insurerName: insurerDisplayName,
                })}
              />
              <p className="result-disclaimer">
                {t(
                  coveragePeriod === "triennial"
                    ? "gate.result.known.disclaimer.triennial"
                    : "gate.result.known.disclaimer",
                  { insurerName: insurerDisplayName }
                )}
              </p>
              <p className="privacy-notice">
                {t("gate.terms")}{" "}
                <a
                  href={`/ch/${locale}/insurance-checker-terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("gate.terms.link")}
                </a>
                {t("gate.terms.suffix")}
              </p>
            </div>
          ) : resultType === "not_covered" ? (
            <div className="result-panel-not-covered">
              <div className="not-covered-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="not-covered-title">
                {t("gate.result.label.not_covered")}
              </div>
              <p className="not-covered-explanation">
                {isPackageNotCovered
                  ? t("gate.result.not_covered.explanation.package", {
                      insurerName: insurerDisplayName,
                      packageName: packageDisplayName,
                    })
                  : t("gate.result.not_covered.explanation", {
                      insurerName: insurerDisplayName,
                    })}
              </p>
              {coverageInfoUrl && (
                <p className="not-covered-link">
                  <a
                    href={coverageInfoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("gate.result.not_covered.check_insurer", {
                      insurerName: insurerDisplayName,
                    })}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                </p>
              )}
              <p className="not-covered-reassurance">
                {t("gate.result.not_covered.reassurance", {
                  insurerName: insurerDisplayName,
                })}
              </p>
            </div>
          ) : resultType === "likely" ? (
            <div className="result-panel-likely">
              <div className="result-estimate-label">
                {t("gate.result.label.likely")}
              </div>
              {likelyReimbursement !== null && service && (
                <>
                  <div className="result-amount">
                    CHF {formatCHF(likelyReimbursement)}
                  </div>
                  <div className="result-breakdown">
                    {membershipPrice > 0 && (
                      <BreakdownItem
                        label={t("gate.result.breakdown.membership")}
                        value={`CHF ${formatCHF(membershipPrice)}`}
                        delay={0.3}
                      />
                    )}
                    <BreakdownItem
                      label={t("gate.result.breakdown.service")}
                      value={`CHF ${formatCHF(servicePrice)}`}
                      delay={membershipPrice > 0 ? 0.32 : 0.3}
                    />
                    {addonTotalPrice > 0 && (
                      <BreakdownItem
                        label={t("gate.result.breakdown.addons")}
                        value={`CHF ${formatCHF(addonTotalPrice)}`}
                        delay={0.35}
                      />
                    )}
                    <BreakdownItem
                      label={t("gate.result.breakdown.save")}
                      value={`CHF ${formatCHF(likelyReimbursement)}`}
                      savings
                      delay={addonTotalPrice > 0 ? 0.4 : 0.4}
                    />
                    <BreakdownItem
                      label={t("gate.result.breakdown.pay")}
                      value={`CHF ${formatCHF(likelyOutOfPocket)}`}
                      delay={addonTotalPrice > 0 ? 0.45 : 0.5}
                    />
                  </div>
                </>
              )}
              <div className="unknown-desc" style={{ marginTop: 8 }}>
                {t("gate.result.likely.desc", {
                  insurerName: insurerDisplayName,
                  packageName: packageDisplayName,
                })}
              </div>
              <p className="result-disclaimer">
                {t(
                  coveragePeriod === "triennial"
                    ? "gate.result.likely.disclaimer.triennial"
                    : "gate.result.likely.disclaimer",
                  { insurerName: insurerDisplayName }
                )}
              </p>
            </div>
          ) : (
            <div className="result-panel-unknown">
              <div className="result-estimate-label">
                {t("gate.result.label.unknown")}
              </div>
              <div className="unknown-desc" style={{ marginTop: 8 }}>
                {t("gate.result.unknown.desc", {
                  insurerName: insurerDisplayName,
                  packageName: packageDisplayName,
                })}
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ delay: 0.4, duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
          className="mt-6"
        >
          {/* Primary CTA deeplinks to booking / membership with the user's
              selection pre-loaded. Parent-page UTMs (e.g. a paid IG ad) are
              forwarded so attribution survives the cross-domain hop.
              See lib/insurance/booking-deeplink.ts. */}
          {service &&
            (() => {
              const bookingHref = buildBookingUrl({
                serviceId: service.id,
                addonProductIds: selectedAddonProductIds,
                incomingSearch:
                  typeof window !== "undefined" ? window.location.search : "",
                flow: bookingFlow,
                country,
                locale,
              })
              const isMembership = isMembershipService(service.id)
              return (
                <a
                  href={bookingHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  onClick={() =>
                    trackEvent("insurance_result_cta_clicked", {
                      cta: isMembership ? "become_member" : "book",
                      source: "coverage_result",
                      service_id: service.id,
                      insurer_id: insurerId,
                      result_type: resultType,
                    })
                  }
                >
                  {t(isMembership ? "gate.cta.become_member" : "gate.cta.book")}
                </a>
              )
            })()}
        </motion.div>

        {/* Cross-insurer comparison (AHD-8461) — directly under the visitor's
            own estimate, so "here is your number" is followed by "here is how
            that compares" while they are still reading figures. The pro forma
            request follows it: that is the action to take once the comparing
            is done. Empty `ranked` → nothing. */}
        {service && ranked.length > 0 && (
          <div id="insurance-comparison" className="mt-6 scroll-mt-24">
            <CoverageComparison
              ranked={ranked}
              userInsurerId={insurerId}
              // Not `getServiceName`: its name carries the LIST price, which
              // would head a table of member-priced rows with the wrong
              // figure. With a membership in the basket the rows reimburse
              // both products, so the subtitle names both — and drops the
              // price, since quoting one of two would be the misleading half.
              serviceName={
                membershipName
                  ? `${t("form.service.membership.label")} ${membershipName} + ${t(
                      `form.service.name.${serviceId}`
                    )}`
                  : `${t(`form.service.name.${serviceId}`)} (CHF ${formatCHF(
                      servicePrice
                    )})`
              }
              insurerId={insurerId}
              serviceId={serviceId}
              summary={
                showCompareNudge
                  ? {
                      title: t("gate.compare.nudge_title", {
                        amount: formatCHF(topInsurer.chf),
                        pct: String(topInsurer.pct),
                      }),
                      sub: t("gate.compare.nudge_sub", {
                        amount: formatCHF(compareDelta),
                      }),
                    }
                  : undefined
              }
            />
          </div>
        )}

        {/* Pro forma request section — shown for every outcome so users can verify directly with their insurer */}
        {showProformaButton && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.4,
              duration: 0.4,
              ease: [0.25, 1, 0.5, 1],
            }}
            className="mt-6"
          >
            <div className="proforma-panel">
              {/* Same icon + title + subtitle row as the compare nudge above.
                  Collapsed it's the trigger; once open it's a plain header and
                  the form's own submit button carries the action. */}
              {(() => {
                const title =
                  t("proforma.request.title") || "Request pro forma invoice"
                const desc =
                  resultType === "known"
                    ? t("proforma.request.desc.known") ||
                      "Want it in writing? A pro forma lets your insurer confirm the exact reimbursement upfront"
                    : resultType === "likely"
                      ? t("proforma.request.desc.likely") ||
                        "Want to be sure? Your insurer can confirm the exact amount with a pro forma"
                      : resultType === "not_covered"
                        ? t("proforma.request.desc.not_covered") ||
                          "Want to double-check anyway? You can submit a pro forma to your insurer directly"
                        : t("proforma.request.desc.unknown") ||
                          "Want to find out for sure? Your insurer can confirm with a pro forma"
                const head = (
                  <>
                    <span className="result-nudge-icon result-nudge-icon-badge">
                      <Invoice weight="regular" aria-hidden="true" />
                    </span>
                    <span className="result-nudge-text">
                      <span className="result-nudge-title">{title}</span>
                      <span className="result-nudge-sub">{desc}</span>
                    </span>
                  </>
                )
                const collapsed = proformaStatus === "idle" && !showProformaForm
                return collapsed ? (
                  <button
                    type="button"
                    className="result-nudge"
                    onClick={() => {
                      setShowProformaForm(true)
                      trackEvent("proforma_form_opened", {
                        insurer_id: insurerId,
                        result_type: resultType,
                      })
                    }}
                  >
                    {head}
                    <span className="result-nudge-cta">
                      {t("proforma.request.cta") || "Request"}
                    </span>
                  </button>
                ) : (
                  <div className="result-nudge result-nudge-static">{head}</div>
                )
              })()}

              {proformaStatus === "idle" && showProformaForm && (
                <div className="proforma-fields">
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <input
                      type="text"
                      value={proformaFirstName}
                      onChange={(e) => setProformaFirstName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleProformaRequest()
                        }
                      }}
                      placeholder={t("proforma.form.firstname") || "First name"}
                      aria-label={t("proforma.form.firstname") || "First name"}
                      className="gate-input"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={proformaLastName}
                      onChange={(e) => setProformaLastName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleProformaRequest()
                        }
                      }}
                      placeholder={t("proforma.form.lastname") || "Last name"}
                      aria-label={t("proforma.form.lastname") || "Last name"}
                      className="gate-input"
                    />
                  </div>
                  <div className="gender-row" style={{ marginTop: 10 }}>
                    <span className="gender-label">
                      {t("proforma.form.gender") || "Gender (optional)"}
                    </span>
                    <div className="gender-pills">
                      <button
                        type="button"
                        className={cn(
                          "gender-pill",
                          proformaGender === "male" && "selected"
                        )}
                        onClick={() =>
                          setProformaGender(
                            proformaGender === "male" ? "" : "male"
                          )
                        }
                      >
                        {t("form.gender.male")}
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "gender-pill",
                          proformaGender === "female" && "selected"
                        )}
                        onClick={() =>
                          setProformaGender(
                            proformaGender === "female" ? "" : "female"
                          )
                        }
                      >
                        {t("form.gender.female")}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-primary btn-primary-sm"
                    onClick={handleProformaRequest}
                    disabled={
                      !proformaFirstName.trim() || !proformaLastName.trim()
                    }
                  >
                    {t("proforma.form.submit") || "Generate invoice"}
                  </button>
                </div>
              )}

              {proformaStatus === "submitting" && (
                <div className="proforma-status" style={{ marginTop: 12 }}>
                  <div className="proforma-spinner" />
                  <span>
                    {t("proforma.status.generating") ||
                      "Generating your invoice..."}
                  </span>
                </div>
              )}

              {proformaStatus === "success" && (
                <div
                  className="proforma-status proforma-success"
                  style={{ marginTop: 12 }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 20, height: 20, color: "#16a34a" }}
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>
                    {proformaPdfUrl
                      ? t("proforma.status.ready", { email }) ||
                        `Your pro forma invoice is ready — we've also sent it to ${email}`
                      : t("proforma.status.queued", { email }) ||
                        `We'll email your invoice to ${email} shortly`}
                  </span>
                  {proformaPdfUrl && (
                    <div className="proforma-invoice-actions">
                      <a
                        href={proformaPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="proforma-download-link"
                        onClick={() =>
                          trackEvent("proforma_viewed", {
                            insurer_id: insurerId,
                            service_id: serviceId,
                          })
                        }
                      >
                        {t("proforma.status.view_link") ||
                          "View pro forma invoice"}
                      </a>
                      <a
                        href={proformaPdfUrl}
                        download="ahead-proforma.pdf"
                        className="proforma-download-link"
                        onClick={() =>
                          trackEvent("proforma_downloaded", {
                            insurer_id: insurerId,
                            service_id: serviceId,
                          })
                        }
                      >
                        {t("proforma.status.download_link") ||
                          "Download pro forma invoice (PDF)"}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {proformaStatus === "async_fallback" && (
                <div
                  className="proforma-status proforma-async"
                  style={{ marginTop: 12 }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>
                    {t("proforma.status.async_fallback", { email }) ||
                      `Taking a bit longer — we'll email the invoice to ${email}`}
                  </span>
                </div>
              )}

              {proformaStatus === "error" && (
                <div
                  className="proforma-status proforma-error"
                  style={{ marginTop: 12 }}
                >
                  <span>{proformaError}</span>
                  <button
                    type="button"
                    className="proforma-retry-btn"
                    onClick={() => {
                      setProformaStatus("idle")
                      setProformaError(null)
                    }}
                  >
                    {t("proforma.error.retry") || "Try again"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        <div className="accordion-restart">
          <button
            type="button"
            className="accordion-restart-btn"
            onClick={handleReset}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            {t("gate.cta.restart")}
          </button>
        </div>
      </div>
    </section>
  )
}

// Reimbursement-assurance reassurance, shown under the estimate on the known
// panel only (AHD-7852) — i.e. confirmed coverage. Deliberately NOT on the
// "likely" panel, where the insurer payout itself is uncertain and there's no
// confirmed amount to guarantee against.
function GuaranteeNote({ text }: { text: string }) {
  return (
    <div className="result-guarantee">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
      <span>{text}</span>
    </div>
  )
}

function BreakdownItem({
  label,
  value,
  savings,
  delay = 0,
}: {
  label: string
  value: string
  savings?: boolean
  delay?: number
}) {
  return (
    <motion.div
      className="breakdown-item"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
    >
      <div className="breakdown-label">{label}</div>
      <div className={cn("breakdown-value", savings && "savings")}>{value}</div>
    </motion.div>
  )
}
